// Shared timestamp normalization for the alert/event time formatters.
//
// Backend timestamps (Databricks / the detection pipeline / the ATS event mirror)
// reach the UI in a few shapes, two of which are NOT valid simplified-ISO-8601:
//   - sub-millisecond precision:  "2026-06-26T21:33:51.862744Z"  (6 fractional digits)
//   - sub-millisecond + offset:   "2026-06-28T23:03:20.890869+00:00"
//   - no zone designator at all:  "2026-04-16T14:39:53"
//
// The ECMAScript date-time string format defines fractional seconds as exactly
// three digits (milliseconds). V8 (Chrome / Edge / Node) leniently accepts more
// and truncates, but Firefox and Safari reject >3 fractional digits as an
// `Invalid Date`. When `new Date(...)` / `Date.parse(...)` returns Invalid Date,
// the alert/event time formatters' `catch` falls back to the RAW string — so the
// Alert Time / Event Time columns rendered an unconverted UTC string and stopped
// reflecting the chosen timezone (only on non-Chromium browsers — "not always").
//
// Normalizing to a standards-compliant form (truncate the fraction to 3 digits,
// and append 'Z' when no zone is present — Databricks writes UTC) makes parsing
// deterministic across every browser. The cases timeline already parses robustly
// via date-fns `parseISO`; this brings the alert-family formatters in line.

/**
 * Return `ts` as a standards-compliant ISO 8601 UTC string that every browser's
 * Date parser accepts identically. Non-strings / empty values are returned as-is.
 */
export function normalizeIsoUtc(ts) {
  if (!ts || typeof ts !== 'string') return ts;
  const hasZone = /Z$|[+-]\d{2}:\d{2}$/.test(ts);
  // Drop sub-millisecond digits: ".862744" -> ".862" (preserves any trailing Z/offset).
  let s = ts.replace(/(\.\d{3})\d+/, '$1');
  // Databricks writes event_time with no zone designator — treat it as UTC.
  if (!hasZone) s += 'Z';
  return s;
}

/**
 * Resolve an IANA zone name for `Intl` / `toLocale*` APIs. The "Local" timezone
 * option carries an EMPTY string — which must NOT be passed as `timeZone`, since
 * an empty (or otherwise invalid) value throws `RangeError: Invalid time zone
 * specified` and crashes the render (or, when try/caught, silently falls back to
 * the raw UTC string). Empty/falsey resolves to the browser-detected zone, then
 * UTC. Mirrors `resolveIana` in TimelineView.jsx — this is the copy the
 * alert-family / sourceEvents formatters use so they don't import the heavy
 * TimelineView module. Any `toLocale*`/`Intl` call reachable while "Local" is
 * selectable MUST route its zone through this. See feedback-local-tz-empty-iana.
 */
export function resolveTz(tzIana) {
  if (tzIana) return tzIana;
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'; }
  catch { return 'UTC'; }
}
